import * as Sentry from "@sentry/react";
import { releaseRecordingLease, renewRecordingLease } from "./api/recordingLease";
import { newLeaseHolder } from "./leaseHolder";
import { forgetVisitMergedAway, noteVisitMergedAway } from "./mergedAwayVisits";
import { reportRecordingLeaseFailure, resetRecordingLeaseFailureReport } from "./recordingLeaseReport";

/**
 * EN-1088 recording lease for the web recorder: while a visit is being recorded on, or a clip recorded on
 * it is still uploading (its clip row does not exist until the upload finishes), the server's lease marks
 * it busy, so merge and bulk archive leave it alone. Strictly best-effort: nothing here awaits the network
 * for the recorder, and every failure is swallowed (the lease self-expires on the server), though one that
 * is not a network failure is reported to Sentry once per recording.
 *
 * Holds are counted per visit: the recording itself, and each upload. A visit is renewed when its first
 * hold starts and every RENEW_MS while held; when its last hold ends it is released after RELEASE_GRACE_MS,
 * which bridges stop to the upload that follows it. Holds on one visit share one holder (a random id sent
 * with every renew and release) for as long as the entry lives; a visit held again after its release gets a
 * new holder, so a release never ends a lease another session took (EN-1448).
 */
export const RENEW_MS = 60_000;
export const RELEASE_GRACE_MS = 15_000;

type Entry = {
    holds: number;
    readonly holder: string;
    // A recording was held on this entry (setRecordingVisit), and an upload (or a discard) accounted for it.
    // An entry that ends recorded and not uploaded means the recording went nowhere (EN-1609).
    recorded: boolean;
    uploaded: boolean;
    renew?: ReturnType<typeof setInterval>;
    release?: ReturnType<typeof setTimeout>;
};
const visits = new Map<string, Entry>();
let recordingVisitId: string | null = null;

// Visits the server says were merged away while held here, with the visit each was merged into.
const mergedInto = new Map<string, string>();
export type RecordingMergedAway = { visitId: string; keptVisitId: string };
let mergedSnapshot: RecordingMergedAway | null = null;
const listeners = new Set<() => void>();
function publish() {
    const kept = recordingVisitId ? mergedInto.get(recordingVisitId) : undefined;
    const next = recordingVisitId && kept ? { visitId: recordingVisitId, keptVisitId: kept } : null;
    if (next?.visitId === mergedSnapshot?.visitId && next?.keptVisitId === mergedSnapshot?.keptVisitId) return;
    mergedSnapshot = next;
    listeners.forEach((l) => l());
}
/** The visit being recorded on, when the server says it was merged away. Stable between changes. */
export const getRecordingMergedAway = () => mergedSnapshot;
export const subscribeRecordingMergedAway = (listener: () => void) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};

/** Runs a lease request without letting it throw or reject into the recorder. */
const quietly = (action: "renew" | "release", request: () => Promise<unknown>) => {
    try {
        void request().catch((error: unknown) => reportRecordingLeaseFailure(error, action));
    } catch (error) {
        reportRecordingLeaseFailure(error, action);
    }
};

function renew(visitId: string, holder: string) {
    quietly("renew", async () => {
        const merged = await renewRecordingLease(visitId, holder);
        if (merged) {
            // No lease is set while the visit is merged away. Renewing carries on, so an unmerge during the
            // recording gets the lease back on the next tick.
            mergedInto.set(visitId, merged.keptVisitId);
            noteVisitMergedAway(visitId, merged.keptVisitId);
        } else if (mergedInto.delete(visitId)) {
            forgetVisitMergedAway(visitId);
        }
        publish();
    });
}

/** Sentry-visible: the lease ended with a recording held on the visit and no upload ever taken. */
function reportIfLost(visitId: string, e: Entry, via: "grace" | "pagehide") {
    if (!e.recorded || e.uploaded) return;
    Sentry.captureMessage("Recording lease released without an upload", {
        level: "warning",
        tags: { errorCategory: "recording", errorId: "recording.lease_released_without_upload" },
        extra: { visitId, via },
    });
}

function hold(visitId: string) {
    const e = visits.get(visitId) ?? { holds: 0, holder: newLeaseHolder(), recorded: false, uploaded: false };
    visits.set(visitId, e);
    e.holds += 1;
    if (e.release) {
        clearTimeout(e.release);
        e.release = undefined;
    }
    if (!e.renew) {
        renew(visitId, e.holder);
        e.renew = setInterval(() => renew(visitId, e.holder), RENEW_MS);
    }
}

function unhold(visitId: string) {
    const e = visits.get(visitId);
    if (!e || e.holds === 0) return;
    e.holds -= 1;
    if (e.holds > 0 || e.release) return;
    e.release = setTimeout(() => {
        if (e.holds > 0) return;
        clearInterval(e.renew);
        visits.delete(visitId);
        reportIfLost(visitId, e, "grace");
        const wasMerged = mergedInto.delete(visitId);
        // A merged-away visit holds no lease (renew sets none while it is merged away): nothing to release.
        if (!wasMerged) quietly("release", () => releaseRecordingLease(visitId, e.holder));
    }, RELEASE_GRACE_MS);
}

/** The visit the recorder is recording on, or null once it stops. Repeating the same value is a no-op. */
export function setRecordingVisit(visitId: string | null) {
    if (visitId === recordingVisitId) return;
    const previous = recordingVisitId;
    recordingVisitId = visitId;
    if (visitId && !previous) resetRecordingLeaseFailureReport();
    if (visitId) {
        hold(visitId);
        const e = visits.get(visitId);
        if (e) {
            // Per-recording state: a discard or upload of an earlier recording must not excuse this one.
            e.recorded = true;
            e.uploaded = false;
        }
    }
    if (previous) unhold(previous);
    publish();
}

/** A recording the vet cancelled is never uploaded on purpose: its lease ending is not a loss. */
export function markRecordingDiscarded(visitId: string) {
    const e = visits.get(visitId);
    if (e) e.uploaded = true;
}

/** Holds the visit's lease for an upload; call the returned function once the upload ends, however it ends. */
export function holdForUpload(visitId: string | null | undefined): () => void {
    if (!visitId) return () => undefined;
    hold(visitId);
    const e = visits.get(visitId);
    if (e) e.uploaded = true;
    let done = false;
    return () => {
        if (done) return;
        done = true;
        unhold(visitId);
    };
}

/** Page is going away: stop renewing and release every lease now (best-effort; the TTL covers a miss). */
export function releaseAllRecordingLeases() {
    visits.forEach((e, visitId) => {
        clearInterval(e.renew);
        clearTimeout(e.release);
        reportIfLost(visitId, e, "pagehide");
        quietly("release", () => releaseRecordingLease(visitId, e.holder, true));
    });
    visits.clear();
    mergedInto.clear();
    recordingVisitId = null;
    publish();
}

if (typeof window !== "undefined") window.addEventListener("pagehide", releaseAllRecordingLeases);

/** Tests only. */
export const resetRecordingLeasesForTest = () => {
    visits.forEach((e) => {
        clearInterval(e.renew);
        clearTimeout(e.release);
    });
    visits.clear();
    mergedInto.clear();
    recordingVisitId = null;
    mergedSnapshot = null;
    listeners.clear();
    resetRecordingLeaseFailureReport();
};
